import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Avatar,
  Paper,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { CiSearch } from 'react-icons/ci';
import { useAuth } from 'src/context/auth/AuthContext';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';
import {
  IoArrowBack,
  IoPersonAddOutline,
  IoTrashOutline,
  IoPencilOutline,
  IoDownloadOutline,
  IoEllipsisVertical,
  IoChevronForward,
  IoSchoolOutline,
  IoShieldCheckmarkOutline,
  IoWarningOutline,
  IoCheckmarkCircleOutline,
} from 'react-icons/io5';
import {
  FaUserGraduate,
  FaChalkboardTeacher,
  FaExchangeAlt,
  FaCoins,
  FaChartLine,
  FaRegIdCard,
} from 'react-icons/fa';
import { MdOutlineAccountBalanceWallet } from 'react-icons/md';

import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import { alpha } from '@mui/material';
import { cohortService, isCohortMember } from 'src/services/cohort.service';
import { classService } from 'src/services/class.service';
import { CohortWithDetails, CohortStudent } from 'src/types/cohort.types';
import { AssignStudentsModal } from 'src/components/cohorts/AssignStudentsModal';
import { AssignTeachersModal } from 'src/components/cohorts/AssignTeachersModal';
import { MoveStudentModal } from 'src/components/cohorts/MoveStudentModal';
import { BulkTransferModal } from 'src/components/cohorts/BulkTransferModal';
import { EditCohortModal } from 'src/components/cohorts/EditCohortModal';
import { DeleteCohortModal } from 'src/components/cohorts/DeleteCohortModal';
import { UnenrollModal } from 'src/components/cohorts/UnenrollModal';

export default function CohortDetail() {
  const { cohortId } = useParams<{ cohortId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, hasPermission, getBaseRoute } = useAuth();

  // Granular RBAC permissions
  const canViewAllCohorts = hasPermission('cohorts.view_all');
  const canEditCohort = hasPermission('cohorts.edit');
  const canDeleteCohort = hasPermission('cohorts.delete');
  const canManageTeachers = hasPermission('cohorts.manage_teachers');
  const canManageStudents = hasPermission('cohorts.manage_students');
  const canViewTeachers = hasPermission('teachers.view_all');

  const baseRoute = getBaseRoute('/cohorts');
  const studentBaseRoute = getBaseRoute('/view-student');
  const teacherBaseRoute = getBaseRoute('/view-teacher');

  const [activeTab, setActiveTab] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'balance' | 'grade'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Modal States
  const [isAssignStudentsOpen, setIsAssignStudentsOpen] = useState(false);
  const [isAssignTeachersOpen, setIsAssignTeachersOpen] = useState(false);
  const [isEditCohortOpen, setIsEditCohortOpen] = useState(false);
  const [isBulkTransferOpen, setIsBulkTransferOpen] = useState(false);
  const [movingStudent, setMovingStudent] = useState<CohortStudent | null>(null);
  const [isDeleteCohortOpen, setIsDeleteCohortOpen] = useState(false);
  const [isUnenrollModalOpen, setIsUnenrollModalOpen] = useState(false);
  const [studentsToUnenroll, setStudentsToUnenroll] = useState<CohortStudent[]>([]);

  // Context Menu for single student row
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeMenuStudent, setActiveMenuStudent] = useState<CohortStudent | null>(null);

  // Fetch all cohorts for transfer lookups & navigation
  const { data: allCohorts = [] } = useQuery({
    queryKey: ['cohorts-list'],
    queryFn: () => cohortService.getCohorts(),
  });

  // Fetch all classes/grades
  const { data: classResponse } = useQuery({
    queryKey: ['classes-list'],
    queryFn: () => classService.getClasses({}),
  });
  const classes = classResponse?.data || [];

  // Active Cohort
  const cohort = useMemo(() => {
    return allCohorts.find((c) => c.id === cohortId) || null;
  }, [allCohorts, cohortId]);

  // Check if logged-in user is authorized for this cohort
  const isAuthorizedForCohort = useMemo(() => {
    if (canViewAllCohorts) return true;
    if (!cohort || !user) return false;
    return [user.id, user.email].some((identifier) => identifier && isCohortMember(cohort, identifier));
  }, [canViewAllCohorts, cohort, user]);

  useHeaderBreadcrumbs(
    cohort
      ? {
          backUrl: baseRoute,
          backLabel: canViewAllCohorts ? 'Cohorts' : 'My Cohorts',
          breadcrumbs: [
            { label: canViewAllCohorts ? 'Cohorts Hub' : 'My Cohorts', path: baseRoute },
            ...(cohort.className
              ? [
                  {
                    label: cohort.className,
                    path: cohort.class_id ? `${baseRoute}?classId=${cohort.class_id}` : baseRoute,
                    isPill: true,
                  },
                ]
              : []),
            { label: cohort.name },
          ],
        }
      : null
  );

  const isLoadingCohort = allCohorts.length === 0 && !cohort;

  // Mutations
  const updateCohortMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: any }) => cohortService.updateCohort(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
      toast.success('Cohort updated successfully!');
      setIsEditCohortOpen(false);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to update cohort.');
    },
  });

  const assignTeachersMutation = useMutation({
    mutationFn: ({ id, teacherIds }: { id: string; teacherIds: string[] }) =>
      cohortService.assignTeachersToCohort(id, teacherIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
      toast.success('Assigned educators updated!');
      setIsAssignTeachersOpen(false);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to update educators.');
    },
  });

  const syncStudentsMutation = useMutation({
    mutationFn: ({
      id,
      studentIds,
      syncProfileGrade,
    }: {
      id: string;
      studentIds: string[];
      syncProfileGrade?: boolean;
    }) => cohortService.syncStudentEnrollments(id, studentIds, syncProfileGrade),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
      toast.success('Scholar enrollments updated!');
      setIsAssignStudentsOpen(false);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to update enrollments.');
    },
  });

  const moveSingleStudentMutation = useMutation({
    mutationFn: ({
      studentId,
      fromCohortId,
      toCohortId,
    }: {
      studentId: string;
      fromCohortId: string;
      toCohortId: string;
    }) => cohortService.moveStudentCohort(studentId, fromCohortId, toCohortId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
      toast.success('Scholar transferred successfully!');
      setMovingStudent(null);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to transfer scholar.');
    },
  });

  const bulkMoveStudentsMutation = useMutation({
    mutationFn: ({
      studentIds,
      fromCohortId,
      toCohortId,
    }: {
      studentIds: string[];
      fromCohortId: string;
      toCohortId: string;
    }) => cohortService.moveStudentsCohort(studentIds, fromCohortId, toCohortId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
      toast.success('Scholars transferred successfully!');
      setSelectedStudentIds([]);
      setIsBulkTransferOpen(false);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to bulk transfer scholars.');
    },
  });

  const unenrollStudentMutation = useMutation({
    mutationFn: ({ cohortId, studentIds }: { cohortId: string; studentIds: string[] }) =>
      cohortService.unenrollStudentsFromCohort(cohortId, studentIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
      toast.success('Scholar(s) removed from cohort section.');
      setSelectedStudentIds([]);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to unenroll scholars.');
    },
  });

  const deleteCohortMutation = useMutation({
    mutationFn: (id: string) => cohortService.deleteCohort(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
      toast.success('Cohort section deleted.');
      navigate('/admin/cohorts');
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to delete cohort.');
    },
  });

  // Calculate Roster Metrics
  const students = cohort?.students || [];
  const totalVaultBalance = students.reduce((sum, s) => sum + (s.balance || 0), 0);
  const avgScholarBalance = students.length > 0 ? Math.round(totalVaultBalance / students.length) : 0;
  const teachers = cohort?.teachers || [];

  // Filter & Sort Students
  const filteredStudents = useMemo(() => {
    let result = [...students];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          `${s.firstname} ${s.lastname}`.toLowerCase().includes(q) ||
          s.email.toLowerCase().includes(q) ||
          s.grade.toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'name') {
        comparison = `${a.firstname} ${a.lastname}`.localeCompare(`${b.firstname} ${b.lastname}`);
      } else if (sortBy === 'balance') {
        comparison = (a.balance || 0) - (b.balance || 0);
      } else if (sortBy === 'grade') {
        comparison = a.grade.localeCompare(b.grade);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [students, searchQuery, sortBy, sortOrder]);

  const handleSelectAll = () => {
    if (selectedStudentIds.length === filteredStudents.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(filteredStudents.map((s) => s.studentId));
    }
  };

  const handleToggleStudent = (studentId: string) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const selectedStudentsObjects = useMemo(() => {
    return students.filter((s) => selectedStudentIds.includes(s.studentId));
  }, [students, selectedStudentIds]);

  const handleExportCSV = () => {
    if (students.length === 0) {
      toast.info('No scholars to export.');
      return;
    }
    const headers = ['First Name', 'Last Name', 'Email', 'Grade', 'Balance'];
    const rows = students.map((s) => [s.firstname, s.lastname, s.email, s.grade, (s.balance || 0).toString()]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `cohort_${cohort?.name || 'roster'}_students.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Roster CSV downloaded successfully!');
  };

  if (isLoadingCohort) {
    return (
      <Page title="Cohort Workspace | Gold Minds Portal">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress sx={{ color: '#EDB42E' }} />
        </Box>
      </Page>
    );
  }

  if (!cohort) {
    return (
      <Page title="Cohort Not Found | Gold Minds Portal">
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Cohort section not found or may have been deleted.
          </Alert>
          <Button
            variant="contained"
            size="small"
            startIcon={<IoArrowBack />}
            onClick={() => navigate(baseRoute)}
            sx={{ bgcolor: palette.primary.main, color: '#FFFFFF', fontWeight: 700 }}
          >
            {canViewAllCohorts ? 'Back to Cohorts Hub' : 'Back to My Cohorts'}
          </Button>
        </Box>
      </Page>
    );
  }

  // Access Guard: Restrict access if user with assigned-only permission is not assigned to this cohort
  if (!isAuthorizedForCohort) {
    return (
      <Page title="Access Restricted | Gold Minds Portal">
        <Box sx={{ p: 4, textAlign: 'center', maxWidth: 640, mx: 'auto' }}>
          <Paper sx={{ p: 4, borderRadius: 2.5, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', textAlign: 'center' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: alpha(palette.warning.main, 0.12),
                color: palette.warning.dark || '#B45309',
                display: 'grid',
                placeItems: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <IoWarningOutline size={26} />
            </Box>
            <Typography variant="h6" fontWeight={800} color="#0F172A" gutterBottom>
              Access Restricted
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You are not currently assigned to <strong>{cohort.name}</strong>. You only have access to your assigned cohort sections.
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<IoArrowBack />}
              onClick={() => navigate(baseRoute)}
              sx={{ bgcolor: palette.primary.main, color: '#FFFFFF', fontWeight: 700, textTransform: 'none' }}
            >
              Return to My Cohorts
            </Button>
          </Paper>
        </Box>
      </Page>
    );
  }

  return (
    <Page title={`${cohort.name} Workspace | Gold Minds Portal`}>
      <Box sx={{ width: '100%', px: { xs: 0.75, md: 1.25 }, pt: 0, pb: 1.5 }}>
        {/* Hero Section Header Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.25 },
            mb: 1.5,
            borderRadius: 2.5,
            border: '1px solid #E2E8F0',
            bgcolor: '#FFFFFF',
            position: 'relative',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1.5}
          >
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                <Typography variant="h5" fontWeight={800} color="#0F172A">
                  {cohort.name}
                </Typography>
                <Chip
                  label={cohort.className || 'Grade Section'}
                  size="small"
                  sx={{
                    bgcolor: '#F1F5F9',
                    color: '#334155',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    borderRadius: 1.25,
                    border: '1px solid #E2E8F0',
                    height: 24,
                  }}
                />
                <Chip
                  label={`${students.length} Scholars Enrolled`}
                  size="small"
                  sx={{
                    bgcolor: alpha(palette.primary.main, 0.08),
                    color: palette.primary.main,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    borderRadius: 1.25,
                    height: 24,
                  }}
                />
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 800, mb: 1, lineHeight: 1.4, fontSize: '0.8125rem' }}>
                {cohort.description || 'Dedicated academic section and classroom economy roster for Gold Minds scholars.'}
              </Typography>

              {/* Assigned Teachers inline strip */}
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Typography variant="caption" fontWeight={700} color="text.secondary" fontSize="0.7rem">
                  EDUCATORS:
                </Typography>
                {teachers.length > 0 ? (
                  teachers.map((t) => (
                    <Chip
                      key={t.id}
                      avatar={
                        <Avatar sx={{ width: 20, height: 20, bgcolor: alpha(palette.secondary.main, 0.2), color: '#854D0E', fontSize: '0.65rem' }}>
                          {t.firstname?.[0]}
                        </Avatar>
                      }
                      label={`${t.firstname} ${t.lastname}`}
                      size="small"
                      onClick={() => {
                        if (canViewTeachers) {
                          navigate(`${teacherBaseRoute}/${t.teacherId || t.id}`);
                        }
                      }}
                      sx={{
                        bgcolor: alpha(palette.secondary.main, 0.12),
                        color: '#854D0E',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 22,
                        cursor: canViewTeachers ? 'pointer' : 'default',
                        '&:hover': { bgcolor: alpha(palette.secondary.main, 0.2) },
                      }}
                    />
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary" fontStyle="italic" fontSize="0.7rem">
                    None assigned yet
                  </Typography>
                )}

                {canManageTeachers && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setIsAssignTeachersOpen(true)}
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: palette.primary.main,
                      p: 0,
                      minWidth: 'auto',
                      ml: 0.25,
                      '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                    }}
                  >
                    Manage Educators →
                  </Button>
                )}
              </Stack>
            </Box>

            {/* Header Action Buttons */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {canEditCohort && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<IoPencilOutline size={15} />}
                  onClick={() => setIsEditCohortOpen(true)}
                  sx={{
                    borderRadius: 1.75,
                    borderColor: '#CBD5E1',
                    color: '#334155',
                    fontWeight: 700,
                    fontSize: '0.78125rem',
                    textTransform: 'none',
                    py: 0.5,
                    '&:hover': { bgcolor: '#F8FAFC', borderColor: '#94A3B8' },
                  }}
                >
                  Edit Section
                </Button>
              )}

              {canManageStudents && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<IoPersonAddOutline size={15} />}
                  onClick={() => setIsAssignStudentsOpen(true)}
                  sx={{
                    borderRadius: 1.75,
                    bgcolor: palette.primary.main,
                    color: '#FFFFFF',
                    fontWeight: 700,
                    fontSize: '0.78125rem',
                    textTransform: 'none',
                    px: 2,
                    py: 0.5,
                    boxShadow: `0 2px 8px ${alpha(palette.primary.main, 0.25)}`,
                    '&:hover': { bgcolor: '#1E5631' },
                  }}
                >
                  Enroll Scholars
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        {/* 4 Financial & Operational KPIs */}
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={6} sm={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #E2E8F0',
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                  bgcolor: alpha(palette.primary.main, 0.08),
                  color: palette.primary.main,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <FaUserGraduate size={17} />
              </Box>
              <Box>
                <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.65rem" letterSpacing={0.3}>
                  TOTAL SCHOLARS
                </Typography>
                <Typography variant="subtitle1" fontWeight={800} color="#0F172A" lineHeight={1.1}>
                  {students.length}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #E2E8F0',
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                  bgcolor: alpha(palette.secondary.main, 0.12),
                  color: '#C48F12',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <MdOutlineAccountBalanceWallet size={19} />
              </Box>
              <Box>
                <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.65rem" letterSpacing={0.3}>
                  COHORT VAULT TOTAL
                </Typography>
                <Typography variant="subtitle1" fontWeight={800} color={palette.primary.main} lineHeight={1.1}>
                  ${totalVaultBalance.toLocaleString()}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #E2E8F0',
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                  bgcolor: alpha(palette.secondary.main, 0.12),
                  color: '#C48F12',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <FaCoins size={17} />
              </Box>
              <Box>
                <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.65rem" letterSpacing={0.3}>
                  AVG SCHOLAR BALANCE
                </Typography>
                <Typography variant="subtitle1" fontWeight={800} color="#0F172A" lineHeight={1.1}>
                  ${avgScholarBalance.toLocaleString()}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid #E2E8F0',
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                  bgcolor: alpha(palette.primary.main, 0.08),
                  color: palette.primary.main,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <FaChalkboardTeacher size={17} />
              </Box>
              <Box>
                <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.65rem" letterSpacing={0.3}>
                  ASSIGNED EDUCATORS
                </Typography>
                <Typography variant="subtitle1" fontWeight={800} color="#0F172A" lineHeight={1.1}>
                  {teachers.length}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Tabbed Navigation Workspace */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: '1px solid #E2E8F0',
            bgcolor: '#FFFFFF',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: '#E2E8F0', px: 2, pt: 0.5, bgcolor: '#FAFAFA' }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                minHeight: 42,
                '& .MuiTab-root': {
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  textTransform: 'none',
                  minHeight: 42,
                  px: 1.75,
                  py: 0.5,
                },
              }}
            >
              <Tab label={`Scholar Roster (${students.length})`} />
              <Tab label={`Assigned Educators (${teachers.length})`} />
              <Tab label="Financial Pulse & Ledger" />
              <Tab label="Section Settings" />
            </Tabs>
          </Box>

          {/* TAB 0: Scholar Roster */}
          {activeTab === 0 && (
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              {/* Roster Controls Toolbar */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                spacing={1.5}
                sx={{ mb: 1.75 }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1 }}>
                  <TextField
                    placeholder="Search scholar by name, email, or grade..."
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ maxWidth: 360, bgcolor: '#FFFFFF' }}
                    InputProps={{
                      sx: { height: 32, fontSize: '0.8125rem' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <CiSearch size={16} color="#64748B" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" fontWeight={600} fontSize="0.75rem">
                    Showing {filteredStudents.length} of {students.length}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<IoDownloadOutline size={14} />}
                    onClick={handleExportCSV}
                    sx={{
                      borderRadius: 1.5,
                      borderColor: '#CBD5E1',
                      color: '#334155',
                      fontWeight: 700,
                      fontSize: '0.725rem',
                      py: 0.4,
                      px: 1.25,
                      textTransform: 'none',
                      '&:hover': { bgcolor: '#F8FAFC' },
                    }}
                  >
                    Export CSV
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<IoPersonAddOutline size={14} />}
                    onClick={() => setIsAssignStudentsOpen(true)}
                    sx={{
                      borderRadius: 1.5,
                      bgcolor: palette.primary.main,
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: '0.725rem',
                      py: 0.4,
                      px: 1.5,
                      textTransform: 'none',
                      '&:hover': { bgcolor: '#1E5631' },
                    }}
                  >
                    + Enroll Scholars
                  </Button>
                </Stack>
              </Stack>

              {/* Floating Multi-Selection Batch Actions Bar */}
              {selectedStudentIds.length > 0 && (
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.25,
                    mb: 1.5,
                    borderRadius: 1.75,
                    bgcolor: '#0F172A',
                    color: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      label={`${selectedStudentIds.length} Selected`}
                      size="small"
                      sx={{ bgcolor: palette.primary.main, color: '#FFFFFF', fontWeight: 800, height: 22, fontSize: '0.7rem' }}
                    />
                    <Typography variant="caption" color="#CBD5E1" fontSize="0.75rem">
                      Choose an action to apply to all selected scholars:
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<FaExchangeAlt size={11} />}
                      onClick={() => setIsBulkTransferOpen(true)}
                      sx={{
                        bgcolor: palette.primary.main,
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '0.725rem',
                        py: 0.3,
                        px: 1.2,
                        borderRadius: 1.25,
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#1E5631' },
                      }}
                    >
                      Transfer Selected ({selectedStudentIds.length})
                    </Button>

                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setStudentsToUnenroll(selectedStudentsObjects);
                        setIsUnenrollModalOpen(true);
                      }}
                      sx={{
                        borderColor: '#EF4444',
                        color: '#F87171',
                        fontWeight: 700,
                        fontSize: '0.725rem',
                        py: 0.3,
                        px: 1,
                        borderRadius: 1.25,
                        textTransform: 'none',
                        '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)', borderColor: '#DC2626' },
                      }}
                    >
                      Unenroll Selected ({selectedStudentIds.length})
                    </Button>

                    <Button
                      size="small"
                      onClick={() => setSelectedStudentIds([])}
                      sx={{ color: '#94A3B8', fontSize: '0.725rem', fontWeight: 600, py: 0.2, minWidth: 'auto' }}
                    >
                      Clear
                    </Button>
                  </Stack>
                </Paper>
              )}

              {/* Roster Data Table */}
              {filteredStudents.length === 0 ? (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 2,
                    border: '1px dashed #CBD5E0',
                    bgcolor: '#F8FAFC',
                  }}
                >
                  <FaUserGraduate size={32} color="#94A3B8" />
                  <Typography variant="subtitle2" fontWeight={700} color="#334155" mt={1}>
                    {searchQuery ? 'No matching scholars found' : 'No Scholars Enrolled in this Section'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                    {searchQuery
                      ? 'Try adjusting your search query or clear the filter.'
                      : 'Enroll scholars to start managing balances, assignments, and classroom economy activities.'}
                  </Typography>
                  {!searchQuery && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setIsAssignStudentsOpen(true)}
                      sx={{
                        bgcolor: palette.primary.main,
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        borderRadius: 1.5,
                        '&:hover': { bgcolor: '#1E5631' },
                      }}
                    >
                      Enroll Scholars Now
                    </Button>
                  )}
                </Paper>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: '1px solid #E2E8F0', borderRadius: 2 }}
                >
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ py: 0.75 }}>
                          <Checkbox
                            color="primary"
                            size="small"
                            indeterminate={
                              selectedStudentIds.length > 0 &&
                              selectedStudentIds.length < filteredStudents.length
                            }
                            checked={
                              filteredStudents.length > 0 &&
                              selectedStudentIds.length === filteredStudents.length
                            }
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#334155', fontSize: '0.725rem', py: 0.75 }}>
                          SCHOLAR
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#334155', fontSize: '0.725rem', py: 0.75 }}>
                          EMAIL ADDRESS
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#334155', fontSize: '0.725rem', py: 0.75 }}>
                          GRADE LEVEL
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: '#334155', fontSize: '0.725rem', py: 0.75 }}>
                          VAULT BALANCE
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: '#334155', fontSize: '0.725rem', py: 0.75 }}>
                          ACTIONS
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredStudents.map((student) => {
                        const isSelected = selectedStudentIds.includes(student.studentId);
                        return (
                          <TableRow
                            key={student.id}
                            hover
                            selected={isSelected}
                            sx={{
                              '&:hover': { bgcolor: '#F8FAFC' },
                              transition: 'background-color 0.15s',
                            }}
                          >
                            <TableCell padding="checkbox" sx={{ py: 0.75 }}>
                              <Checkbox
                                color="primary"
                                size="small"
                                checked={isSelected}
                                onChange={() => handleToggleStudent(student.studentId)}
                              />
                            </TableCell>

                            {/* Scholar Name & Avatar */}
                            <TableCell sx={{ py: 0.75 }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => navigate(`${studentBaseRoute}/${student.studentId || student.id}`)}
                              >
                                <Avatar
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    bgcolor: alpha(palette.primary.main, 0.08),
                                    color: palette.primary.main,
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                  }}
                                >
                                  {student.firstname?.[0]}
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight={700}
                                    color="#0F172A"
                                    fontSize="0.8125rem"
                                    sx={{ '&:hover': { color: palette.primary.main, textDecoration: 'underline' } }}
                                  >
                                    {student.firstname} {student.lastname}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" fontSize="0.6875rem">
                                    ID: {student.studentId.slice(0, 8)}...
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>

                            {/* Email */}
                            <TableCell sx={{ py: 0.75 }}>
                              <Typography variant="body2" color="text.secondary" fontSize="0.78125rem">
                                {student.email || 'No email registered'}
                              </Typography>
                            </TableCell>

                            {/* Grade */}
                            <TableCell sx={{ py: 0.75 }}>
                              <Chip
                                label={student.grade ? `Grade ${student.grade}` : 'Grade 7'}
                                size="small"
                                sx={{
                                  bgcolor: '#F1F5F9',
                                  color: '#334155',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  height: 20,
                                  borderRadius: 1,
                                }}
                              />
                            </TableCell>

                            {/* Balance */}
                            <TableCell align="right" sx={{ py: 0.75 }}>
                              <Typography variant="subtitle2" fontWeight={800} color={palette.primary.main} fontSize="0.8125rem">
                                ${(student.balance || 0).toLocaleString()}
                              </Typography>
                            </TableCell>

                            {/* Action Buttons */}
                            <TableCell align="right" sx={{ py: 0.75 }}>
                              <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<FaExchangeAlt size={10} />}
                                  onClick={() => setMovingStudent(student)}
                                  sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    color: palette.primary.main,
                                    borderColor: alpha(palette.primary.main, 0.3),
                                    borderRadius: 1.25,
                                    py: 0.2,
                                    px: 0.8,
                                    height: 24,
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: alpha(palette.primary.main, 0.08), borderColor: palette.primary.main },
                                  }}
                                >
                                  Transfer
                                </Button>

                                <Tooltip title="More options">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      setMenuAnchorEl(e.currentTarget);
                                      setActiveMenuStudent(student);
                                    }}
                                    sx={{ color: '#64748B', p: 0.4 }}
                                  >
                                    <IoEllipsisVertical size={15} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* TAB 1: Assigned Educators */}
          {activeTab === 1 && (
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={800} color="#0F172A">
                    Assigned Educators ({teachers.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Teachers who have instructional access and ledger management over this cohort.
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setIsAssignTeachersOpen(true)}
                  sx={{
                    bgcolor: palette.primary.main,
                    color: '#FFFFFF',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    borderRadius: 1.75,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#1E5631' },
                  }}
                >
                  Manage Assigned Teachers
                </Button>
              </Stack>

              {teachers.length === 0 ? (
                <Paper sx={{ p: 3.5, textAlign: 'center', borderRadius: 2, border: '1px dashed #CBD5E0' }}>
                  <FaChalkboardTeacher size={32} color="#94A3B8" />
                  <Typography variant="subtitle2" fontWeight={700} color="#334155" mt={1}>
                    No Educators Assigned
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                    Assign teachers to manage check-ins, street credits, and student financial milestones.
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setIsAssignTeachersOpen(true)}
                    sx={{
                      bgcolor: palette.primary.main,
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      borderRadius: 1.5,
                      '&:hover': { bgcolor: '#1E5631' },
                    }}
                  >
                    Assign Teachers Now
                  </Button>
                </Paper>
              ) : (
                <Grid container spacing={1.5}>
                  {teachers.map((t) => (
                    <Grid item xs={12} sm={6} md={4} key={t.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid #E2E8F0',
                          bgcolor: '#FAFAFA',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.25,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              bgcolor: alpha(palette.secondary.main, 0.15),
                              color: '#854D0E',
                              fontWeight: 800,
                              fontSize: '1rem',
                            }}
                          >
                            {t.firstname?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={800} color="#0F172A">
                              {t.firstname} {t.lastname}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" fontSize="0.725rem">
                              {t.email}
                            </Typography>
                          </Box>
                        </Stack>

                        <Divider />
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Chip
                            label={t.subject || 'Lead Educator'}
                            size="small"
                            sx={{ bgcolor: '#F1F5F9', color: '#334155', fontWeight: 600, fontSize: '0.7rem', height: 22, borderRadius: 1 }}
                          />
                          {canViewTeachers && (
                            <Button
                              size="small"
                              onClick={() => navigate(`${teacherBaseRoute}/${t.teacherId || t.id}`)}
                              sx={{ fontSize: '0.725rem', fontWeight: 700, textTransform: 'none', p: 0, color: palette.primary.main }}
                            >
                              Staff Profile →
                            </Button>
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* TAB 2: Financial Pulse & Ledger */}
          {activeTab === 2 && (
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={800} color="#0F172A">
                  Cohort Economy & Financial Health
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Aggregate financial status, distribution channels, and virtual economy activity for this section.
                </Typography>
              </Box>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}
                  >
                    <Typography variant="subtitle2" fontWeight={800} color="#0F172A" sx={{ mb: 1.25 }}>
                      Classroom Currency Reserves
                    </Typography>
                    <Stack spacing={1.25}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">Total Active Roster Balance:</Typography>
                        <Typography variant="caption" fontWeight={800} color={palette.primary.main}>
                          ${totalVaultBalance.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">Average Scholar Balance:</Typography>
                        <Typography variant="caption" fontWeight={800} color="#0F172A">
                          ${avgScholarBalance.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">Total Enrolled Scholars:</Typography>
                        <Typography variant="caption" fontWeight={800} color="#0F172A">
                          {students.length}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}
                  >
                    <Typography variant="subtitle2" fontWeight={800} color="#0F172A" sx={{ mb: 1.25 }}>
                      Classroom Financial Governance
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Automated daily wage credits, job payroll disbursements, and classroom infraction debits are tracked centrally.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setActiveTab(0)}
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: 1.5,
                        borderColor: '#CBD5E1',
                        color: '#334155',
                      }}
                    >
                      Inspect Scholar Ledger →
                    </Button>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* TAB 3: Section Settings & Lifecycle */}
          {activeTab === 3 && (
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                  <Typography variant="subtitle1" fontWeight={800} color="#0F172A">
                    Section Configuration & Governance
                  </Typography>
                  <Chip
                    label="Master Directory Sync"
                    size="small"
                    sx={{
                      bgcolor: alpha(palette.primary.main, 0.08),
                      color: palette.primary.main,
                      fontWeight: 700,
                      fontSize: '0.725rem',
                      height: 22,
                      borderRadius: 1,
                    }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                  Update title, change parent grade tier, review data retention guarantees, or manage section lifecycle status for {cohort.name}.
                </Typography>
              </Box>

              <Grid container spacing={2.5}>
                {/* Left Column: Configuration & Metadata */}
                <Grid item xs={12} lg={7}>
                  <Stack spacing={2.5}>
                    {/* Card 1: Section Identity & Academic Tier */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 2.5,
                        border: '1px solid #E2E8F0',
                        bgcolor: '#FFFFFF',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={800} color="#0F172A">
                            Cohort Name & Metadata
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Primary naming and grade classification within school directory
                          </Typography>
                        </Box>
                        {canEditCohort && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<IoPencilOutline size={14} />}
                            onClick={() => setIsEditCohortOpen(true)}
                            sx={{
                              borderRadius: 1.75,
                              borderColor: '#CBD5E1',
                              color: '#334155',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              textTransform: 'none',
                              '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' },
                            }}
                          >
                            Edit Section Details
                          </Button>
                        )}
                      </Stack>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          gap: 2,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: '#F8FAFC',
                          border: '1px solid #F1F5F9',
                        }}
                      >
                        <Box>
                          <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.675rem" letterSpacing={0.4}>
                            CURRENT NAME
                          </Typography>
                          <Typography variant="body2" fontWeight={800} color="#0F172A" sx={{ mt: 0.25 }}>
                            {cohort.name}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.675rem" letterSpacing={0.4}>
                            PARENT GRADE LEVEL
                          </Typography>
                          <Box sx={{ mt: 0.25 }}>
                            <Chip
                              label={cohort.className || 'Grade Section'}
                              size="small"
                              sx={{
                                bgcolor: '#FFFFFF',
                                color: '#334155',
                                fontWeight: 700,
                                fontSize: '0.725rem',
                                height: 22,
                                border: '1px solid #E2E8F0',
                                borderRadius: 1,
                              }}
                            />
                          </Box>
                        </Box>

                        <Box sx={{ gridColumn: { xs: '1', sm: 'span 2' } }}>
                          <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.675rem" letterSpacing={0.4}>
                            SECTION DESCRIPTION
                          </Typography>
                          <Typography variant="body2" color="#334155" sx={{ mt: 0.25, fontSize: '0.8125rem' }}>
                            {cohort.description || 'No section description provided. Click Edit Section Details to add one.'}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.675rem" letterSpacing={0.4}>
                            SECTION REFERENCE ID
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 0.25,
                              display: 'block',
                              fontFamily: 'monospace',
                              color: '#64748B',
                              fontSize: '0.725rem',
                              bgcolor: '#FFFFFF',
                              p: 0.5,
                              borderRadius: 1,
                              border: '1px solid #E2E8F0',
                              maxWidth: 'fit-content',
                            }}
                          >
                            {cohort.id}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.675rem" letterSpacing={0.4}>
                            CREATION DATE
                          </Typography>
                          <Typography variant="body2" color="#334155" sx={{ mt: 0.25, fontSize: '0.8125rem', fontWeight: 600 }}>
                            {cohort.createdat ? new Date(cohort.createdat).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'Active Section'}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {/* Card 2: Classroom Economy & Financial Policies */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 2.5,
                        border: '1px solid #E2E8F0',
                        bgcolor: '#FFFFFF',
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={800} color="#0F172A" sx={{ mb: 0.5 }}>
                        Classroom Economy & Payroll Links
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Scholars enrolled in {cohort.name} participate in classroom marketplace, payroll, and tax deductions.
                      </Typography>

                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ p: 1.75, borderRadius: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                            <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.675rem">
                              COMBINED SECTION VAULT
                            </Typography>
                            <Typography variant="h6" fontWeight={800} color={palette.primary.main} sx={{ my: 0.25 }}>
                              ${totalVaultBalance.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                              Across {students.length} enrolled scholars (~${avgScholarBalance.toLocaleString()} avg)
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box sx={{ p: 1.75, borderRadius: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                            <Typography variant="caption" fontWeight={700} color="#64748B" fontSize="0.675rem">
                              PAYROLL & TRANSACTIONS
                            </Typography>
                            <Typography variant="body2" fontWeight={800} color="#0F172A" sx={{ my: 0.4 }}>
                              Automated Paychecks
                            </Typography>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => navigate('/admin/payroll')}
                              sx={{
                                p: 0,
                                fontWeight: 700,
                                fontSize: '0.725rem',
                                color: palette.primary.main,
                                textTransform: 'none',
                                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                              }}
                            >
                              Open Payroll Hub →
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Stack>
                </Grid>

                {/* Right Column: Safeguards & Danger Zone */}
                <Grid item xs={12} lg={5}>
                  <Stack spacing={2.5}>
                    {/* Card 3: Roster Safeguards & Policy */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 2.5,
                        border: '1px solid #E2E8F0',
                        bgcolor: '#FFFFFF',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            bgcolor: alpha(palette.primary.main, 0.08),
                            color: palette.primary.main,
                            display: 'grid',
                            placeItems: 'center',
                          }}
                        >
                          <IoShieldCheckmarkOutline size={18} />
                        </Box>
                        <Typography variant="subtitle1" fontWeight={800} color="#0F172A">
                          Data Retention Guarantee
                        </Typography>
                      </Stack>

                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.5, mb: 1.5 }}>
                        Scholar balances, transaction histories, and Street Cred points will remain completely intact even if sections are adjusted or deleted.
                      </Typography>

                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
                          <IoCheckmarkCircleOutline size={16} color="#166534" />
                          <Typography variant="caption" fontWeight={700} color="#166534">
                            Zero Financial Balance Loss
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="#15803D" fontSize="0.75rem" lineHeight={1.4}>
                          Deleting this section will unassign all enrolled scholars and teachers. Student financial accounts, balances, and transaction histories will remain completely intact.
                        </Typography>
                      </Box>
                    </Paper>

                    {/* Card 4: Danger Zone (Delete Cohort Permission Only) */}
                    {canDeleteCohort && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          borderRadius: 2.5,
                          border: '1px solid #FECACA',
                          bgcolor: '#FEF2F2',
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1.5,
                              bgcolor: '#FEE2E2',
                              color: '#DC2626',
                              display: 'grid',
                              placeItems: 'center',
                            }}
                          >
                            <IoWarningOutline size={18} />
                          </Box>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={800} color="#991B1B">
                              Danger Zone: Delete Cohort
                            </Typography>
                            <Typography variant="caption" color="#7F1D1D">
                              Permanent section removal
                            </Typography>
                          </Box>
                        </Stack>

                        <Typography variant="body2" color="#7F1D1D" sx={{ my: 1.5, fontSize: '0.8125rem', lineHeight: 1.45 }}>
                          Deleting this section will unassign {students.length} enrolled scholars and {teachers.length} teachers. Student financial accounts, balances, and transaction histories will remain completely intact.
                        </Typography>

                        <Button
                          variant="contained"
                          color="error"
                          fullWidth
                          startIcon={<IoTrashOutline size={16} />}
                          onClick={() => setIsDeleteCohortOpen(true)}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 700,
                            fontSize: '0.8125rem',
                            py: 1,
                            textTransform: 'none',
                            bgcolor: '#DC2626',
                            boxShadow: '0 2px 8px rgba(220, 38, 38, 0.25)',
                            '&:hover': { bgcolor: '#B91C1C' },
                          }}
                        >
                          Delete {cohort.name}
                        </Button>
                      </Paper>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>

        {/* Row Context Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={() => {
            setMenuAnchorEl(null);
            setActiveMenuStudent(null);
          }}
          PaperProps={{ sx: { borderRadius: 2, minWidth: 180 } }}
        >
          <MenuItem
            onClick={() => {
              if (activeMenuStudent) {
                navigate(`${studentBaseRoute}/${activeMenuStudent.studentId || activeMenuStudent.id}`);
              }
              setMenuAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <FaRegIdCard size={15} />
            </ListItemIcon>
            <ListItemText primary="View Student Profile" />
          </MenuItem>

          <MenuItem
            onClick={() => {
              if (activeMenuStudent) {
                setMovingStudent(activeMenuStudent);
              }
              setMenuAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <FaExchangeAlt size={13} />
            </ListItemIcon>
            <ListItemText primary="Transfer to Section" />
          </MenuItem>

          <Divider />

          <MenuItem
            onClick={() => {
              if (activeMenuStudent) {
                setStudentsToUnenroll([activeMenuStudent]);
                setIsUnenrollModalOpen(true);
              }
              setMenuAnchorEl(null);
            }}
            sx={{ color: '#EF4444' }}
          >
            <ListItemIcon sx={{ color: '#EF4444' }}>
              <IoTrashOutline size={15} />
            </ListItemIcon>
            <ListItemText primary="Unenroll from Cohort" />
          </MenuItem>
        </Menu>

        {/* Modals */}
        <AssignStudentsModal
          open={isAssignStudentsOpen}
          onClose={() => setIsAssignStudentsOpen(false)}
          cohort={cohort}
          allCohorts={allCohorts}
          onConfirm={async (cohortId, studentIds, syncProfileGrade) => {
            await syncStudentsMutation.mutateAsync({ id: cohortId, studentIds, syncProfileGrade });
          }}
          isSubmitting={syncStudentsMutation.isPending}
        />

        <AssignTeachersModal
          open={isAssignTeachersOpen}
          onClose={() => setIsAssignTeachersOpen(false)}
          cohort={cohort}
          onConfirm={async (cohortId, teacherIds) => {
            await assignTeachersMutation.mutateAsync({ id: cohortId, teacherIds });
          }}
          isSubmitting={assignTeachersMutation.isPending}
        />

        <EditCohortModal
          open={isEditCohortOpen}
          onClose={() => setIsEditCohortOpen(false)}
          cohort={cohort}
          classes={classes}
          onSubmit={async (cohortId, dto) => {
            await updateCohortMutation.mutateAsync({ id: cohortId, dto });
          }}
          isSubmitting={updateCohortMutation.isPending}
        />

        <DeleteCohortModal
          open={isDeleteCohortOpen}
          onClose={() => setIsDeleteCohortOpen(false)}
          cohort={cohort}
          onConfirmDelete={async (id) => {
            await deleteCohortMutation.mutateAsync(id);
            setIsDeleteCohortOpen(false);
          }}
          isDeleting={deleteCohortMutation.isPending}
        />

        <UnenrollModal
          open={isUnenrollModalOpen}
          onClose={() => {
            setIsUnenrollModalOpen(false);
            setStudentsToUnenroll([]);
          }}
          cohort={cohort}
          studentsToUnenroll={studentsToUnenroll}
          onConfirmUnenroll={async (ids) => {
            if (cohort) {
              await unenrollStudentMutation.mutateAsync({ cohortId: cohort.id, studentIds: ids });
              setIsUnenrollModalOpen(false);
              setStudentsToUnenroll([]);
            }
          }}
          isUnenrolling={unenrollStudentMutation.isPending}
        />

        <MoveStudentModal
          open={!!movingStudent}
          onClose={() => setMovingStudent(null)}
          student={movingStudent}
          currentCohort={cohort}
          availableCohorts={allCohorts}
          onConfirmMove={async (studentId, fromCohortId, toCohortId) => {
            await moveSingleStudentMutation.mutateAsync({ studentId, fromCohortId, toCohortId });
          }}
          isSubmitting={moveSingleStudentMutation.isPending}
        />

        <BulkTransferModal
          open={isBulkTransferOpen}
          onClose={() => setIsBulkTransferOpen(false)}
          selectedStudents={selectedStudentsObjects}
          currentCohort={cohort}
          availableCohorts={allCohorts}
          onConfirmBulkMove={async (studentIds, fromCohortId, toCohortId) => {
            await bulkMoveStudentsMutation.mutateAsync({ studentIds, fromCohortId, toCohortId });
          }}
          isSubmitting={bulkMoveStudentsMutation.isPending}
        />
      </Box>
    </Page>
  );
}
